import { useTranslation } from '@/hooks/useTranslation';
import { Card } from 'primereact/card';

interface MetricCardProps {
  icon: string;
  value: string | number;
  label: string;
  delta?: {
    value: number;
    label: string;
    isPositive?: boolean;
  };
  variant?:
    | 'default'
    | 'comments'
    | 'likes'
    | 'shares'
    | 'participants'
    | 'responses'
    | 'time'
    | 'completion';
  layout?: 'vertical' | 'horizontal';
}

export const MetricCard = ({
  icon,
  value,
  label,
  delta,
  variant = 'default',
  layout = 'vertical',
}: MetricCardProps) => {
  const { t } = useTranslation();

  const getDeltaClass = () => {
    if (!delta) return '';
    if (delta.isPositive === undefined) return 'neutral';
    return delta.isPositive ? 'positive' : 'negative';
  };

  // Build accessible label for screen readers
  const getAriaLabel = () => {
    let ariaLabel = t('reporting.aria.metricValue', {
      label,
      value: String(value),
    });
    if (delta) {
      const direction = delta.isPositive !== false ? 'up' : 'down';
      ariaLabel += `. ${t('reporting.aria.metricDelta', { direction, value: Math.abs(delta.value), label: delta.label })}`;
    }
    return ariaLabel;
  };

  return (
    <Card
      className={`metric-card metric-card-${variant} metric-card-${layout}`}
      role='group'
      aria-label={getAriaLabel()}
      tabIndex={0}
    >
      <i className={`pi ${icon}`} aria-hidden='true'></i>
      <div className='metric-content'>
        <div className='metric-info'>
          <span className='label'>{label}</span>
          <span className='value'>{value}</span>
        </div>
        {delta && layout === 'horizontal' && (
          <span className={`delta-pill ${getDeltaClass()}`} aria-hidden='true'>
            <i
              className={`pi ${delta.isPositive !== false ? 'pi-arrow-up' : 'pi-arrow-down'}`}
            ></i>
            {Math.abs(delta.value)} {delta.label}
          </span>
        )}
        {delta && layout === 'vertical' && (
          <span className={`delta ${getDeltaClass()}`} aria-hidden='true'>
            <i
              className={`pi ${delta.isPositive !== false ? 'pi-arrow-up' : 'pi-arrow-down'}`}
            ></i>
            {Math.abs(delta.value)} {delta.label}
          </span>
        )}
      </div>
    </Card>
  );
};
